import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Pencil, FileText, Upload, Download, Trash2, File, Eye } from 'lucide-react';
import { toast } from 'sonner';

const DOC_TYPES = ['MSME Certificate', 'Establishment Certificate', 'GST Certificate', 'PT Certificate', 'Networth Certificate', 'Notices', 'Other'];

export default function ClientViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [client, setClient] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({ document_type: '', expiry_date: '' });

  const fetchData = async () => {
    try {
      const [clientRes, docsRes] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/documents?client_id=${id}`)
      ]);
      setClient(clientRes.data);
      setDocuments(docsRes.data);
    } catch {
      toast.error('Failed to load client');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [id]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!uploadForm.document_type) {
      toast.error('Please select a document type first');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('client_id', id);
    formData.append('document_type', uploadForm.document_type);
    formData.append('expiry_date', uploadForm.expiry_date || '');
    try {
      await api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded successfully');
      setUploadForm({ document_type: '', expiry_date: '' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc._id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await api.delete(`/documents/${docId}`);
      toast.success('Document deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const isExpired = (date) => date && new Date(date) < new Date();

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>;
  if (!client) return <p className="text-center text-olive-400 py-8">Client not found</p>;

  const InfoRow = ({ label, value }) => (
    <div className="flex flex-col sm:flex-row sm:items-center py-3 border-b border-[#E6EDE6] last:border-0">
      <span className="text-xs tracking-wide uppercase font-semibold text-[#4F734F] sm:w-40 mb-1 sm:mb-0">{label}</span>
      <span className="text-sm text-[#213021]">{value || '-'}</span>
    </div>
  );

  // Group documents by type
  const docsByType = {};
  documents.forEach(doc => {
    const type = doc.document_type || 'Other';
    if (!docsByType[type]) docsByType[type] = [];
    docsByType[type].push(doc);
  });

  return (
    <div data-testid="client-view-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/admin/clients')} className="p-2 hover:bg-olive-50 rounded-sm" data-testid="back-btn">
            <ArrowLeft size={20} className="text-olive-600" />
          </button>
          <div>
            <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">{client.full_name}</h1>
            <div className="flex items-center gap-2 mt-1">
              {(client.client_types || []).map(t => (
                <span key={t} className="bg-olive-100 text-olive-700 rounded-full px-2 py-0.5 text-xs">{t}</span>
              ))}
            </div>
          </div>
        </div>
        <Link to={`/admin/clients/${id}/edit`}>
          <Button className="btn-primary gap-2" data-testid="edit-client-btn"><Pencil size={14} /> Edit</Button>
        </Link>
      </div>

      <Tabs defaultValue="info" className="space-y-6">
        <TabsList>
          <TabsTrigger value="info" data-testid="tab-client-info">Client Info</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-client-docs">
            Documents ({documents.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E6EDE6] rounded-sm p-6">
              <h2 className="font-heading text-lg font-medium text-[#213021] mb-4">Client Information</h2>
              <InfoRow label="Company" value={client.company_name} />
              <InfoRow label="Contact" value={client.contact_number} />
              <InfoRow label="Email" value={client.email} />
              <InfoRow label="Address" value={client.address} />
              <InfoRow label="Pincode" value={client.pincode} />
            </div>
            <div className="bg-white border border-[#E6EDE6] rounded-sm p-6">
              <h2 className="font-heading text-lg font-medium text-[#213021] mb-4">Tax & Registration Details</h2>
              <InfoRow label="GST Number" value={client.gst_number} />
              <InfoRow label="Personal PAN" value={client.personal_pan} />
              <InfoRow label="Company PAN" value={client.company_pan} />
              <InfoRow label="TAN Number" value={client.tan_number} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="documents">
          <div className="space-y-6">
            {/* Upload Section */}
            <div className="bg-white border border-[#E6EDE6] rounded-sm p-6" data-testid="doc-upload-section">
              <h2 className="font-heading text-lg font-medium text-[#213021] mb-4">Upload New Document</h2>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase font-semibold text-[#4F734F]">Document Type</Label>
                  <Select value={uploadForm.document_type || 'none'} onValueChange={(v) => setUploadForm(prev => ({ ...prev, document_type: v === 'none' ? '' : v }))}>
                    <SelectTrigger className="input-field" data-testid="doc-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" disabled>Select type</SelectItem>
                      {DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase font-semibold text-[#4F734F]">Expiry Date (optional)</Label>
                  <Input
                    type="date"
                    value={uploadForm.expiry_date}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, expiry_date: e.target.value }))}
                    className="input-field"
                    data-testid="doc-expiry-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase font-semibold text-[#4F734F]">Choose File</Label>
                  <Input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleUpload}
                    className="input-field"
                    disabled={uploading || !uploadForm.document_type}
                    data-testid="doc-file-input"
                  />
                </div>
                <div>
                  {uploading && (
                    <div className="flex items-center gap-2 text-sm text-olive-500">
                      <div className="w-4 h-4 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                      Uploading...
                    </div>
                  )}
                </div>
              </div>
              {!uploadForm.document_type && (
                <p className="text-xs text-olive-400 mt-2">Select a document type before uploading</p>
              )}
            </div>

            {/* Documents Grouped by Type */}
            {documents.length === 0 ? (
              <div className="bg-white border border-[#E6EDE6] rounded-sm p-8 text-center">
                <File size={40} className="mx-auto text-olive-300 mb-3" />
                <p className="text-sm text-olive-400">No documents uploaded for this client</p>
                <p className="text-xs text-olive-300 mt-1">Upload certificates, notices, and other documents above</p>
              </div>
            ) : (
              <div className="space-y-4">
                {DOC_TYPES.map(type => {
                  const typeDocs = docsByType[type];
                  if (!typeDocs || typeDocs.length === 0) return null;
                  return (
                    <div key={type} className="bg-white border border-[#E6EDE6] rounded-sm" data-testid={`doc-group-${type.toLowerCase().replace(/\s+/g, '-')}`}>
                      <div className="px-5 py-3 bg-[#F4F7F4] border-b border-[#E6EDE6]">
                        <h3 className="text-sm font-semibold text-[#213021]">{type}</h3>
                        <span className="text-xs text-olive-500">{typeDocs.length} document{typeDocs.length > 1 ? 's' : ''}</span>
                      </div>
                      <div className="divide-y divide-[#E6EDE6]">
                        {typeDocs.map(doc => (
                          <div key={doc._id} className="flex items-center gap-4 px-5 py-3 hover:bg-[#F8FAF7] transition-colors" data-testid={`doc-item-${doc._id}`}>
                            <FileText size={18} className="text-olive-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#213021] truncate">{doc.original_filename}</p>
                              <div className="flex items-center gap-3 mt-0.5">
                                <span className="text-xs text-olive-500">
                                  Uploaded: {doc.upload_date ? new Date(doc.upload_date).toLocaleDateString('en-IN') : '-'}
                                </span>
                                {doc.expiry_date && (
                                  <span className={`text-xs ${isExpired(doc.expiry_date) ? 'text-red-600 font-medium' : 'text-olive-500'}`}>
                                    Expiry: {new Date(doc.expiry_date).toLocaleDateString('en-IN')}
                                    {isExpired(doc.expiry_date) && ' (EXPIRED)'}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleDownload(doc)}
                                className="p-1.5 hover:bg-olive-100 rounded-sm"
                                title="Download"
                                data-testid={`download-doc-${doc._id}`}
                              >
                                <Download size={14} className="text-olive-500" />
                              </button>
                              <button
                                onClick={() => handleDeleteDoc(doc._id)}
                                className="p-1.5 hover:bg-red-50 rounded-sm"
                                title="Delete"
                                data-testid={`delete-doc-${doc._id}`}
                              >
                                <Trash2 size={14} className="text-red-400" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
